<script>
import { mapState } from 'vuex'
import { fetchList } from '@apis/admin/lang'
import { fetchConfig } from '@apis/admin/config'
import PageNavigation from '@components/admin/page-navigation'
import LangItem from '@components/admin/lang-search-item'
import fromPairs from 'lodash/fromPairs'
import filter from 'lodash/filter'

export default {
  components: { PageNavigation, LangItem },
  props: {
    allow_select: {
      type: Boolean,
      default: false,
    },
    modal_check: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      config: null,
      list: [],
      total_count: 0,
      page: 1,
      total_page: 0,
      page_navigation: {},
      search_params: {},
    }
  },
  computed: {
    ...mapState('common', {
      constants: (state) => state.constants,
    }),
    support_locales() {
      if (!this.config) return []

      return filter(
        this.constants.lang.locales,
        ([locale]) => this.config.support_locales.indexOf(locale) !== -1
      )
    },
  },
  watch: {
    $route(to, from) {
      this.page = this.$route.query.page || 1
      this.loadList().catch((e) => this.$toasted.error(e.message))
    },
  },
  mounted() {
    this.init()
  },
  methods: {
    init() {
      this.page = this.$route.query.page || 1
      this.search_params = { ...this.$route.query }
      Promise.all([this.loadList(), this.loadConfig()])
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    async loadList() {
      this.loading = true
      let ret_obj = null
      if (this.$route.name === 'admin-lang-list') {
        ret_obj = await fetchList({
          ...this.$route.params,
          ...this.$route.query,
          search_target: 'value',
        })
      } else {
        ret_obj = await fetchList({
          ...this.$route.params,
          page: this.page,
          list_count: 5,
          search_keyword: this.search_params.search_keyword,
          search_target: 'value',
        })
      }

      this.list = ret_obj.data
      this.total_count = ret_obj.total_count
      this.total_page = ret_obj.total_page
      this.page_navigation = ret_obj.page_navigation
    },
    async loadConfig() {
      const { config } = await fetchConfig('service')

      this.config = config
    },
    handleReload() {
      Promise.all([this.loadList()])
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    search() {
      if (this.$route.name === 'admin-lang-list') {
        this.$router.push({ query: this.search_params })
      } else {
        this.handleReload()
      }
    },
    handleSelect(name) {
      this.$emit('select', name)
    },
    handlePageChange(val) {
      this.page = val
      this.handleReload()
    },
    fromPairs,
  },
}
</script>

<template>
  <div v-if="config">
    <h3 :class="$style.xeAdmin">{{ $t('admin.search') }}</h3>
    <form @submit.prevent="search">
      <table cellspacing="0" cellpadding="0" :class="$style.rowTable">
        <colgroup>
          <col width="20%" />
          <col width="30%" />
          <col width="20%" />
          <col width="30%" />
        </colgroup>
        <tbody>
          <tr>
            <th scope="col">
              <div>{{ $t('admin.content') }}</div>
            </th>
            <td>
              <input
                v-model="search_params.search_keyword"
                :placeholder="$t('admin.search_word')"
                type="text"
                :class="[$style.inputTypeText, $style.fullWidth]"
              />
            </td>
            <th scope="col"
              ><div>{{ $t('admin.locale') }}</div></th
            >
            <td>
              <select v-model="search_params.locale">
                <option
                  v-for="val in [
                    [undefined, $t('admin.total')],
                    ...support_locales,
                  ]"
                  :key="val[1]"
                  :value="val[0]"
                  >{{ val[1] }}</option
                >
              </select>
            </td>
          </tr>
          <tr>
            <th colspan="4" :class="$style.button">
              <a
                href="#"
                :class="[$style.button, $style.red]"
                @click.prevent="
                  search_params = {}
                  search
                "
                ><span>{{ $t('admin.reset') }}</span></a
              >
              &nbsp;
              <span :class="[$style.button, $style.black]"
                ><input type="submit" :value="$t('admin.submit')"
              /></span>
            </th>
          </tr>
        </tbody>
      </table>
    </form>
    <h3 :class="$style.xeAdmin">{{ $t('admin.new_enroll') }}</h3>
    <table cellspacing="0" cellpadding="0" :class="$style.crossTable">
      <tbody>
        <LangItem :locales="support_locales" @create="handleReload"></LangItem>
      </tbody>
    </table>
    <h3 :class="$style.xeAdmin">{{ $t('admin.enroll_lang') }}</h3>
    <table cellspacing="0" cellpadding="0" :class="$style.adminTable">
      <caption v-if="!loading"
        >Total {{ total_count }}, Page {{ page }}/{{ total_page }}</caption
      >
      <thead>
        <tr>
          <th scope="col" rowspan="2"
            ><div>{{ $t('admin.user_name') }}</div></th
          >
          <th scope="col" :colspan="support_locales.length"
            ><div>{{ $t('admin.value') }}</div></th
          >
          <th scope="col" rowspan="2"><div>Action</div></th>
        </tr>
        <tr>
          <th
            v-for="[locale, title] in support_locales"
            :key="locale"
            scope="col"
            ><div>{{ title }}</div></th
          >
        </tr>
      </thead>
      <tbody>
        <LangItem
          v-for="val in list"
          :key="val.name"
          :locales="support_locales"
          :name="val.name"
          @delete="handleReload"
        >
          <template v-if="allow_select" slot="button" slot-scope="{ action }">
            &nbsp;
            <a
              v-if="action === 'READ'"
              href="#"
              :class="[$style.button]"
              @click.prevent="handleSelect(val.name)"
              ><span>{{ $t('select') }}</span></a
            >
          </template>
        </LangItem>
        <tr v-if="!list.length">
          <td :colspan="support_locales.length + 2">
            <div :class="$style.tCenter">{{ $t('admin.no_lang') }}</div>
          </td>
        </tr>
      </tbody>
    </table>
    <PageNavigation
      v-if="!loading"
      :total_count="total_count"
      :total_page="page_navigation.total_page"
      :cur_page="page"
      :page_count="page_navigation.page_count"
      :modal_check="modal_check"
      @change="handlePageChange"
    ></PageNavigation>
  </div>
</template>

<style lang="scss" module>
@import '@src/design/default.scss';
@import '@src/design/admin.scss';
@import '@src/design/font.scss';
@import '@src/design/button.scss';
</style>
